import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AlertTriangle, ShieldAlert, CalendarClock, CheckCircle2, Snowflake, ListChecks, Timer, PencilLine, X, RotateCcw, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  getNotifications,
  dismissNotification,
  dismissAllNotifications,
  undoDismissNotification,
  restoreAllNotifications,
} from "@/lib/api";
import { appPath } from "@/lib/paths";
import { useT } from "@/i18n/I18nContext";

const ICONS = {
  ccp_overdue: CalendarClock,
  plan_overdue: CalendarClock,
  reception_overdue: CalendarClock,
  haccp_breach: ShieldAlert,
  maturation_ready: Snowflake,
  task: ListChecks,
  lot_expiring: Timer,
  audit_change: PencilLine,
  ccp_escalation: ShieldAlert,
};

const sevCls = {
  critical: "badge-bad",
  warning: "badge-warn",
  info: "bg-secondary text-muted-foreground border border-border",
};

const SEV_RANK = { critical: 0, warning: 1, info: 2 };
const GROUP_MIN = 3; // collapse a type into one line once it has this many reminders

/** Group reminders by type (when there are at least GROUP_MIN of a kind); keeps severity order. */
function groupItems(items) {
  const byType = {};
  items.forEach((n) => { (byType[n.type] = byType[n.type] || []).push(n); });
  const out = [];
  const seen = new Set();
  items.forEach((n) => {
    if (seen.has(n.type)) return;
    const arr = byType[n.type];
    if (arr.length >= GROUP_MIN) {
      seen.add(n.type);
      const severity = arr.reduce((w, x) => (SEV_RANK[x.severity] < SEV_RANK[w] ? x.severity : w), arr[0].severity);
      out.push({ group: true, type: n.type, severity, items: arr, id: `group-${n.type}` });
    } else {
      out.push(n);
    }
  });
  return out;
}

export const NotificationCenter = () => {
  const navigate = useNavigate();
  const { t } = useT();
  const [data, setData] = useState({ count: 0, items: [], dismissed_count: 0 });
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({}); // type -> bool (group expanded)

  const load = useCallback(() => {
    getNotifications().then(setData).catch((e) => console.error("notifications: load failed", e));
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000); // refresh every minute
    return () => clearInterval(id);
  }, [load]);

  const go = (link) => {
    setOpen(false);
    navigate(appPath(link));
  };

  // Optimistically hide the item, persist on the server, and offer an undo.
  const dismiss = async (e, n) => {
    e.stopPropagation();
    e.preventDefault();
    setData((d) => {
      const items = d.items.filter((x) => x.id !== n.id);
      return { ...d, items, count: items.length, dismissed_count: (d.dismissed_count || 0) + 1 };
    });
    try {
      await dismissNotification(n.id, n.state_key || "");
      toast.success(t("notif.dismissed"), {
        action: {
          label: t("notif.undo"),
          onClick: () => undoDismissNotification(n.id).then(load).catch(() => toast.error(t("notif.undoFailed"))),
        },
      });
    } catch {
      toast.error(t("notif.dismissFailed"));
      load();
    }
  };

  const dismissAll = async () => {
    const items = data.items.map((n) => ({ id: n.id, state_key: n.state_key || "" }));
    if (!items.length) return;
    setData((d) => ({ ...d, items: [], count: 0, dismissed_count: (d.dismissed_count || 0) + items.length }));
    try {
      await dismissAllNotifications(items);
      toast.success(t("notif.allDismissed", { n: items.length }), {
        action: {
          label: t("notif.undo"),
          onClick: () => restoreAllNotifications().then(load).catch(() => toast.error(t("notif.undoFailed"))),
        },
      });
    } catch {
      toast.error(t("notif.dismissFailed"));
      load();
    }
  };

  // Dismiss every reminder in a collapsed group at once.
  const dismissGroup = async (e, g) => {
    e.stopPropagation();
    e.preventDefault();
    const ids = new Set(g.items.map((x) => x.id));
    setData((d) => {
      const items = d.items.filter((x) => !ids.has(x.id));
      return { ...d, items, count: items.length, dismissed_count: (d.dismissed_count || 0) + ids.size };
    });
    try {
      await dismissAllNotifications(g.items.map((x) => ({ id: x.id, state_key: x.state_key || "" })));
      toast.success(t("notif.allDismissed", { n: ids.size }), {
        action: { label: t("notif.undo"), onClick: () => restoreAllNotifications().then(load).catch(() => toast.error(t("notif.undoFailed"))) },
      });
    } catch {
      toast.error(t("notif.dismissFailed"));
      load();
    }
  };

  const restoreAll = async () => {
    try {
      await restoreAllNotifications();
      load();
    } catch {
      toast.error(t("notif.undoFailed"));
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notifications-button">
          <Bell className="h-5 w-5" />
          {data.count > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[hsl(var(--danger))] px-1 text-[10px] font-semibold text-white"
              data-testid="notifications-badge"
            >
              {data.count > 9 ? "9+" : data.count}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" collisionPadding={8} className="w-[min(340px,calc(100vw-16px))] p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <span className="text-sm font-semibold">{t("notif.reminders")}</span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{t("notif.active", { n: data.count })}</span>
            {data.items.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={dismissAll}
                data-testid="notifications-dismiss-all"
              >
                {t("notif.dismissAll")}
              </Button>
            )}
          </div>
        </div>
        <div className="max-h-[380px] overflow-y-auto thin-scroll">
          {data.items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
              <CheckCircle2 className="h-8 w-8 text-[hsl(var(--success))]" />
              <p className="text-sm text-muted-foreground">{t("notif.allCaughtUp")}</p>
            </div>
          ) : (
            groupItems(data.items).map((n) => {
              const Icon = ICONS[n.type] || AlertTriangle;
              if (n.group) {
                const isOpen = !!expanded[n.type];
                const Chevron = isOpen ? ChevronDown : ChevronRight;
                return (
                  <div key={n.id} className="border-b border-border" data-testid={`notification-group-${n.type}`}>
                    <div className="group flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-secondary">
                      <button
                        type="button"
                        onClick={() => setExpanded((x) => ({ ...x, [n.type]: !isOpen }))}
                        aria-expanded={isOpen}
                        className="flex min-w-0 flex-1 items-start gap-3 text-left"
                        data-testid={`notification-group-toggle-${n.type}`}
                      >
                        <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${sevCls[n.severity]}`}>
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5 text-sm font-medium">
                            <span className="truncate">{t(`notif.group.${n.type}`, { n: n.items.length })}</span>
                            <span className="shrink-0 rounded-full bg-secondary px-1.5 text-[11px] font-semibold text-foreground" data-testid={`notification-group-count-${n.type}`}>{n.items.length}</span>
                          </span>
                          <span className="block truncate text-xs text-muted-foreground">
                            {isOpen ? t("notif.collapse") : n.items.slice(0, 3).map((x) => x.title.split(": ").slice(1).join(": ") || x.title).join(" · ")}
                          </span>
                        </span>
                        <Chevron className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => dismissGroup(e, n)}
                        title={t("notif.dismissGroup")}
                        aria-label={t("notif.dismissGroup")}
                        className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-70 transition-colors hover:bg-background hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover:opacity-100"
                        data-testid={`notification-group-dismiss-${n.type}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    {isOpen && (
                      <div className="bg-secondary/40" data-testid={`notification-group-items-${n.type}`}>
                        {n.items.map((x) => (
                          <div key={x.id} className="group flex w-full items-start gap-3 border-t border-border/60 py-2 pl-9 pr-4 text-left transition-colors hover:bg-secondary" data-testid={`notification-item-${x.type}`}>
                            <button type="button" onClick={() => go(x.link)} className="flex min-w-0 flex-1 items-start gap-3 text-left" data-testid={`notification-open-${x.id}`}>
                              <span className="min-w-0">
                                <span className="block truncate text-sm">{x.title}</span>
                                <span className="block text-xs text-muted-foreground">{x.message}</span>
                              </span>
                            </button>
                            <button
                              type="button"
                              onClick={(e) => dismiss(e, x)}
                              title={t("notif.dismiss")}
                              aria-label={t("notif.dismiss")}
                              className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-70 transition-colors hover:bg-background hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover:opacity-100"
                              data-testid={`notification-dismiss-${x.id}`}
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <div
                  key={n.id}
                  className="group flex w-full items-start gap-3 border-b border-border px-4 py-3 text-left transition-colors hover:bg-secondary"
                  data-testid={`notification-item-${n.type}`}
                >
                  <button
                    type="button"
                    onClick={() => go(n.link)}
                    className="flex min-w-0 flex-1 items-start gap-3 text-left"
                    data-testid={`notification-open-${n.id}`}
                  >
                    <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${sevCls[n.severity]}`}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">{n.title}</span>
                      <span className="block text-xs text-muted-foreground">{n.message}</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => dismiss(e, n)}
                    title={t("notif.dismiss")}
                    aria-label={t("notif.dismiss")}
                    className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-70 transition-colors hover:bg-background hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover:opacity-100"
                    data-testid={`notification-dismiss-${n.id}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              );
            })
          )}
        </div>
        <div className="flex items-center justify-between border-t border-border px-4 py-2 text-[11px] text-muted-foreground">
          <span>{t("notif.digest")}</span>
          {data.dismissed_count > 0 && (
            <button
              type="button"
              onClick={restoreAll}
              className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="notifications-restore-all"
            >
              <RotateCcw className="h-3 w-3" />
              {t("notif.restoreAll", { n: data.dismissed_count })}
            </button>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
